/**
 * Cliente API para el módulo de POS (catálogo, clientes, órdenes).
 * Usa el cliente HTTP base con credentials.
 */

import { api } from '@/lib/api';
import type {
  AcabadoOrden,
  Tamano as TamanoShared,
  EstadoOrden,
  MetodoPago,
  MetodoPagoConfirmado,
} from '@carwash-pos/shared';

export type Tamano = TamanoShared;

// ============================================================
// Tipos compartidos (espejo del backend)
// ============================================================

export interface Sucursal {
  id: number;
  name: string;
  direccion?: string | null;
  telefono?: string | null;
  horario?: string | null;
}

export interface OrdenAdministrativa {
  id: number;
  folio: string;
  sucursal_id: number;
  sucursal_nombre: string;
  estado: EstadoOrden;
  metodo_pago: MetodoPago;
  total: number;
  fecha_orden: string;
  es_contingencia: number;
  cliente_nombre: string;
  vehiculo_marca: string | null;
  vehiculo_modelo: string | null;
  vehiculo_placas: string | null;
}

export interface CursorOrdenesAdministrativas {
  fecha: string;
  id: number;
}

export type RangoOrdenesAdministrativas =
  | { periodo: 1 | 7 | 14 | 30 }
  | { fecha_inicio: string; fecha_fin: string };

export interface FiltrosOrdenesAdministrativas {
  sucursalId: number | null;
  rango: RangoOrdenesAdministrativas;
  estados: EstadoOrden[];
  incluirContingencias: boolean;
  incluirTotal: boolean;
  cursor?: CursorOrdenesAdministrativas | null;
}

export interface RespuestaOrdenesAdministrativas {
  fecha_inicio: string;
  fecha_fin: string;
  sucursal_id: number | null;
  estados: EstadoOrden[];
  incluir_contingencias: boolean;
  por_pagina: 50;
  total: number | null;
  total_paginas: number | null;
  ordenes: OrdenAdministrativa[];
  siguiente_cursor: CursorOrdenesAdministrativas | null;
}

export interface Manager {
  id: number;
  nombre: string;
  email: string;
  telefono: string | null;
  status: 'active' | 'inactive';
  sucursal_ids: number[];
  sucursales: Array<{ id: number; name: string }>;
}

export interface Lavador {
  id: number;
  nombre: string;
  celular?: string | null;
  sucursal_id?: number;
  activo?: boolean;
}

export interface MovimientoComision {
  id: number;
  monto: number;
  tipo: 'pago' | 'consumo' | 'reversa';
  motivo: string | null;
  descripcion: string | null;
  reversa_de_id: number | null;
  reversa_de_tipo: 'pago' | 'consumo' | null;
  created_at: string;
  registrado_por: number;
  registrado_por_nombre: string;
  operacion_id: number;
}

export interface ServicioLavador {
  id: number;
  nombre: string;
  estado: 'pendiente' | 'en_proceso' | 'pausado' | 'completado' | 'cancelado';
  comision: number;
  inicio_proceso_at: string | null;
  completado_at: string | null;
  duracion_segundos: number | null;
  duracion_calculada_at?: string | null;
  vehiculo: { marca: string; modelo: string; placas: string | null } | null;
}

export interface Cliente {
  id: number;
  nombre: string;
  apellido: string;
  celular: string;
  email?: string | null;
  notas?: string | null;
  vehiculosCount?: number;
  matchedByVehicle?: Vehiculo;
}

export interface Vehiculo {
  id: number;
  marca?: string;
  modelo: string;
  color?: string | null;
  placas?: string | null;
  tipoTamano: Tamano;
  /** Si true, este vehículo es el sentinel "Sin vehículo" (id=1). */
  esSinVehiculo?: boolean;
  /** Display formateado "marca - modelo" o solo "modelo" si no hay marca. */
  display?: string;
  /** Cuando viene en una búsqueda, indica con qué vehículo coincidió. */
  matchedVehicle?: {
    id: number;
    marca?: string;
    modelo: string;
    color?: string | null;
    placas?: string | null;
    tipoTamano: Tamano;
    display?: string;
  };
}

export function formatearVehiculo(
  vehiculo: Pick<Vehiculo, 'marca' | 'modelo' | 'esSinVehiculo'> | null | undefined,
): string {
  if (!vehiculo || vehiculo.esSinVehiculo) return 'Sin vehículo';
  const limpiar = (valor: string | null | undefined) =>
    (valor ?? '')
      .trim()
      .replace(/^-+\s*/, '')
      .replace(/\s*-+$/, '');
  return [limpiar(vehiculo.marca), limpiar(vehiculo.modelo)].filter(Boolean).join(' - ');
}

export interface VehiculoCatalogo {
  id: number;
  marca: string;
  modelo: string;
  /** Display formateado "marca - modelo". */
  display: string;
}

export interface MarcaCatalogo {
  marca: string;
  total: number;
}

export interface PaquetePrecio {
  tamano: Tamano | 'unico';
  precioBase: number;
  precioFinal: number;
  tienePromo: boolean;
}

export interface Paquete {
  id: number;
  nombre: string;
  descripcion?: string | null;
  duracionMin: number;
  comisionPct: number;
  color?: string | null;
  /** null si tiene variantes por talla; valor si es precio fijo. */
  precioFijo: number | null;
  precios: PaquetePrecio[];
}

export interface Aroma {
  id: number;
  nombre: string;
}

export interface AcabadoOrdenActualizado {
  ok: true;
  brillo_exterior: boolean;
  aroma_id: number | null;
  aroma: Aroma | null;
}

export interface NotificacionVehiculoListo {
  id: number;
  estado: 'pendiente' | 'notificada';
  ultima_apertura_at: string | null;
  notificada_at: string | null;
}

export interface OrdenCreada {
  id: number;
  folio: string;
  sucursal_id: number;
  cliente_id: number;
  vehiculo_id: number;
  estado: 'draft';
  entrega_estimada_at: string | null;
  subtotal: number;
  total: number;
}

export interface CambioServicio {
  id: number;
  estado?: string;
  lavador_id?: number | null;
  orden_id?: number;
  orden_estado?: Orden['estado'];
  orden_completada_ahora?: boolean;
  notificacion_vehiculo_listo?: NotificacionVehiculoListo | null;
}

export interface ServicioCompleto {
  id: number;
  paqueteId: number | null;
  paqueteNombre: string;
  paqueteCustomNombre: string | null;
  paqueteCustomPrecio: number | null;
  tamano: Tamano | null;
  precioBase: number;
  precioFinal: number;
  descuentoPct: number;
  descuentoMonto: number;
  precioPersonalizado: boolean;
  lavadorId: number | null;
  lavadorNombre: string | null;
  estado: 'pendiente' | 'en_proceso' | 'pausado' | 'completado' | 'cancelado';
  inicio_proceso_at: string | null;
  completado_at: string | null;
  duracion_segundos: number | null;
  duracion_calculada_at: string | null;
}

export interface PagoOrden {
  id: number;
  metodo: Exclude<MetodoPago, 'mixto' | 'pendiente'>;
  monto: number;
  referencia: string | null;
  efectivo_recibido: number | null;
  cambio: number | null;
  created_at: string;
}

export interface EventoEstadoOrden {
  id: number;
  estado_anterior: EstadoOrden | null;
  estado: EstadoOrden;
  detalle: string | null;
  created_at: string;
  usuario: { id: number; nombre: string } | null;
}

export interface Orden {
  id: number;
  folio: string;
  sucursal_id: number;
  cliente_id: number;
  vehiculo_id: number;
  estado: EstadoOrden;
  metodo_pago: MetodoPago | null;
  pago_referencia: string | null;
  pago_monto_recibido: number | null;
  pago_cambio: number | null;
  brillo_exterior: boolean;
  aroma_id: number | null;
  aroma: Aroma | null;
  subtotal: number;
  total: number;
  notas: string | null;
  created_by: number;
  confirmed_by: number | null;
  confirmed_at: string | null;
  completed_by: number | null;
  completed_at: string | null;
  en_espera_by: number | null;
  en_espera_at: string | null;
  entregado_by: number | null;
  entregado_at: string | null;
  entrega_estimada_at: string | null;
  estado_at: string | null;
  /** Fecha del servicio cuando fue capturado tras una contingencia. */
  fecha_operativa?: string | null;
  contingencia_motivo?: string | null;
  pagos: PagoOrden[];
  monto_pagado: number;
  saldo_pendiente: number;
  notificacion_vehiculo_listo: NotificacionVehiculoListo | null;
  created_at: string;
  updated_at: string;
  puede_corregir_pago?: boolean;
  /** Cliente asociado a la orden (con su celular para WhatsApp). */
  cliente?: Cliente | null;
  /** Vehículo asociado a la orden (puede ser el sentinel). */
  vehiculo?: Vehiculo | null;
  servicios: ServicioCompleto[];
  servicios_count?: number;
  timeline?: EventoEstadoOrden[];
}

// ============================================================
// API endpoints
// ============================================================

export const catalogApi = {
  aromas: () => api.get<{ aromas: Aroma[] }>('/catalog/aromas'),
  paquetes: (sucursalId: number) =>
    api.get<{ paquetes: Paquete[] }>(`/catalog/paquetes?sucursal_id=${sucursalId}`),
  lavadores: (sucursalId: number) =>
    api.get<{ lavadores: Lavador[] }>(`/catalog/lavadores?sucursal_id=${sucursalId}`),
  promociones: (sucursalId: number) =>
    api.get<{ promociones: unknown[] }>(`/catalog/promociones/activas?sucursal_id=${sucursalId}`),
  vehiculosPosibles: (clienteId: number) =>
    api.get<{ vehiculos: Vehiculo[] }>(`/catalog/vehiculos-posibles?cliente_id=${clienteId}`),
  vehiculosCatalogo: (q: string) =>
    api.get<{ vehiculos: VehiculoCatalogo[] }>(`/catalog/vehiculos?q=${encodeURIComponent(q)}`),
  marcas: (q = '') =>
    api.get<{ marcas: MarcaCatalogo[] }>(`/catalog/marcas?q=${encodeURIComponent(q)}`),
  modelos: (marca: string, q = '') =>
    api.get<{ modelos: VehiculoCatalogo[] }>(
      `/catalog/modelos?marca=${encodeURIComponent(marca)}&q=${encodeURIComponent(q)}`,
    ),
  agregarVehiculoCatalogo: (data: { marca: string; modelo: string }) =>
    api.post<{ vehiculo: VehiculoCatalogo; created: boolean }>('/catalog/vehiculos', data),
};

export const clientesApi = {
  search: (q: string) => api.get<{ clientes: Cliente[] }>(`/clientes?q=${encodeURIComponent(q)}`),
  frecuentes: (sucursalId: number) =>
    api.get<{ clientes: Cliente[] }>(`/clientes/frecuentes?sucursal_id=${sucursalId}`),
  get: (id: number) => api.get<{ cliente: Cliente; vehiculos: Vehiculo[] }>(`/clientes/${id}`),
  actualizar: (id: number, data: { nombre: string; apellido: string; celular: string }) =>
    api.patch<{ cliente: Cliente }>(`/clientes/${id}`, data),
  create: (data: {
    nombre: string;
    apellido: string;
    celular: string;
    email?: string;
    notas?: string;
    vehiculo?: {
      marca?: string;
      modelo: string;
      color?: string;
      placas?: string;
      tipoTamano: Tamano;
    };
  }) => api.post<{ cliente: Cliente; vehiculo: Vehiculo | null }>('/clientes', data),
  addVehiculo: (
    clienteId: number,
    data: {
      marca?: string;
      modelo: string;
      color?: string;
      placas?: string;
      tipoTamano: Tamano;
    },
  ) => api.post<{ vehiculo: Vehiculo }>(`/clientes/${clienteId}/vehiculos`, data),
  eliminarVehiculo: (clienteId: number, vehiculoId: number) =>
    api.delete<{ ok: true }>(`/clientes/${clienteId}/vehiculos/${vehiculoId}`),
};

export const sucursalesApi = {
  listar: () => api.get<{ sucursales: Sucursal[] }>('/sucursales'),
};

export interface ResumenOperacion {
  ordenes_count: number;
  servicios_count: number;
  ingreso_efectivo: number;
  ingreso_tarjeta: number;
  ingreso_transferencia: number;
  ingreso_pendiente: number;
  total_ventas: number;
  comisiones_total: number;
}

export interface OperacionSucursal {
  id: number;
  sucursal_id: number;
  operador_id: number;
  estado: 'abierta';
  inicio_at: string;
  sucursal_nombre?: string;
  operador_nombre?: string;
}

export interface ResumenDiarioOrdenes {
  ventas: {
    ordenes: number;
    servicios: number;
    total: number;
  };
  cobradas: {
    ordenes: number;
    servicios: number;
    total: number;
  };
  ingresos_cobrados: number;
  por_cobrar: {
    ordenes: number;
    total: number;
  };
}

export interface OperacionAdministrativa extends OperacionSucursal {
  sucursal_nombre: string;
  operador_nombre: string;
  sesion_vigente: boolean;
}

export interface CierreHistorico {
  registro_id: string;
  operacion_id: number | null;
  tipo: 'corte' | 'sin_ventas' | 'liberada';
  estado: 'cerrada_con_corte' | 'cerrada_sin_ventas' | 'liberada' | 'corte_historico';
  sucursal_id: number;
  sucursal_nombre: string;
  operador_id: number;
  operador_nombre: string;
  cerrado_por: number | null;
  cerrado_por_nombre: string | null;
  inicio_at: string | null;
  cierre_at: string;
  motivo_cierre: string | null;
  ventas_sin_corte: number;
  corte_id: number | null;
  servicios_count: number | null;
  ingreso_efectivo: number | null;
  ingreso_tarjeta: number | null;
  ingreso_transferencia: number | null;
  ingreso_pendiente: number | null;
  comisiones_total: number | null;
  efectivo_contado: number | null;
  tarjeta_contado: number | null;
  transferencia_contado: number | null;
  diferencia: number | null;
  diferencia_tarjeta: number | null;
  diferencia_transferencia: number | null;
  diferencia_total: number | null;
  notas: string | null;
}

export interface OrdenPendienteCorte {
  id: number;
  folio: string;
  cliente_nombre: string;
  vehiculo_etiqueta: string;
  vehiculo_placas: string | null;
  total: number;
  created_at: string;
}

export interface ResultadoCorte extends ResumenOperacion {
  total_esperado: number;
  total_declarado: number;
  efectivo_contado: number;
  tarjeta_contado: number;
  transferencia_contado: number;
  diferencia: number;
  diferencia_tarjeta: number;
  diferencia_transferencia: number;
  diferencia_total: number;
  notas: string | null;
}

export const operacionesApi = {
  actual: () => api.get<{ operacion: OperacionSucursal | null }>('/operaciones/actual'),
  abrir: (sucursalId: number) =>
    api.post<{ operacion: OperacionSucursal }>('/operaciones/abrir', { sucursal_id: sucursalId }),
  cerrarSinVentas: () => api.post<{ ok: true }>('/operaciones/cerrar-sin-ventas'),
  corte: (data: {
    sucursal_id: number;
    efectivo_contado: number;
    tarjeta_contado: number;
    transferencia_contado: number;
    notas?: string;
  }) =>
    api.post<{
      ok: true;
      resumen: ResultadoCorte;
      ordenes_pendientes: OrdenPendienteCorte[];
    }>('/operaciones/corte', data),
  activas: () => api.get<{ operaciones: OperacionAdministrativa[] }>('/operaciones/activas'),
  liberar: (id: number, motivo: string) =>
    api.post<{ ok: true; incidencia: boolean }>(`/operaciones/${id}/liberar`, { motivo }),
  cierres: (filtros: {
    fecha_inicio: string;
    fecha_fin: string;
    sucursal_id: number | null;
    tipo: 'todos' | 'corte' | 'sin_ventas' | 'liberada';
    pagina?: number;
    por_pagina?: number;
  }) => {
    const query = new URLSearchParams({
      fecha_inicio: filtros.fecha_inicio,
      fecha_fin: filtros.fecha_fin,
      tipo: filtros.tipo,
    });
    if (filtros.sucursal_id) query.set('sucursal_id', String(filtros.sucursal_id));
    if (filtros.pagina) query.set('pagina', String(filtros.pagina));
    if (filtros.por_pagina) query.set('por_pagina', String(filtros.por_pagina));
    return api.get<{
      fecha_inicio: string;
      fecha_fin: string;
      sucursal_id: number | null;
      tipo: 'todos' | 'corte' | 'sin_ventas' | 'liberada';
      pagina: number;
      por_pagina: number;
      total: number;
      total_paginas: number;
      cierres: CierreHistorico[];
    }>(`/operaciones/cierres?${query.toString()}`);
  },
};

export const managersApi = {
  listar: () => api.get<{ managers: Manager[] }>('/managers'),
  crear: (data: { nombre: string; email: string; telefono?: string; sucursal_ids: number[] }) =>
    api.post<{ manager: Manager; contrasena_temporal: string }>('/managers', data),
  editar: (
    id: number,
    data: {
      nombre?: string;
      telefono?: string | null;
      status?: 'active' | 'inactive';
      sucursal_ids?: number[];
    },
  ) => api.patch<{ manager: Manager }>(`/managers/${id}`, data),
  restablecerContrasena: (id: number) =>
    api.post<{ ok: true; contrasena_temporal: string }>(`/managers/${id}/restablecer-contrasena`),
};

export interface PaqueteAdmin {
  id: number;
  nombre: string;
  descripcion: string | null;
  color: string | null;
  precio_fijo: number | null;
  tipo_precio: 'fijo' | 'por_tamano';
  precios: Partial<Record<Tamano, number>>;
  activo: boolean;
}

export interface PromocionAdmin {
  id: number;
  paquete_id: number | null;
  paquete_nombre: string | null;
  nombre: string;
  descuento_tipo: 'porcentaje' | 'fijo';
  descuento_valor: number;
  fecha_inicio: string;
  fecha_fin: string;
  sucursal_ids: number[];
  activo: boolean;
}

export interface PaqueteAdminPayload {
  nombre: string;
  descripcion?: string | null;
  color?: string | null;
  tipo_precio: 'fijo' | 'por_tamano';
  precio_fijo?: number;
  precios?: Record<Tamano, number>;
  activo?: boolean;
}

export interface PromocionAdminPayload {
  paquete_id: number;
  nombre: string;
  descuento_tipo: 'porcentaje' | 'fijo';
  descuento_valor: number;
  fecha_inicio: string;
  fecha_fin: string;
  sucursal_ids: number[];
  activo?: boolean;
}

export interface VehiculoCatalogoManual {
  id: number;
  marca: string;
  modelo: string;
  created_at: string;
}

export interface VehiculoCatalogoManualPayload {
  marca: string;
  modelo: string;
}

export const catalogoAdminApi = {
  paquetes: () => api.get<{ paquetes: PaqueteAdmin[] }>('/admin/catalogo/paquetes'),
  crearPaquete: (data: PaqueteAdminPayload) =>
    api.post<{ paquete_id: number }>('/admin/catalogo/paquetes', data),
  editarPaquete: (id: number, data: PaqueteAdminPayload) =>
    api.patch<{ ok: true }>(`/admin/catalogo/paquetes/${id}`, data),
  promociones: () => api.get<{ promociones: PromocionAdmin[] }>('/admin/catalogo/promociones'),
  crearPromocion: (data: PromocionAdminPayload) =>
    api.post<{ promocion_id: number }>('/admin/catalogo/promociones', data),
  editarPromocion: (id: number, data: PromocionAdminPayload) =>
    api.patch<{ ok: true }>(`/admin/catalogo/promociones/${id}`, data),
  vehiculos: (q = '') =>
    api.get<{ vehiculos: VehiculoCatalogoManual[] }>(
      `/admin/catalogo/vehiculos${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
  editarVehiculo: (id: number, data: VehiculoCatalogoManualPayload) =>
    api.patch<{ ok: true }>(`/admin/catalogo/vehiculos/${id}`, data),
  retirarVehiculo: (id: number) => api.delete<{ ok: true }>(`/admin/catalogo/vehiculos/${id}`),
};

export const dashboardApi = {
  resumen: (sucursalId?: number) => {
    const query = sucursalId ? `?sucursal_id=${sucursalId}` : '';
    return api.get<{
      fecha: string;
      sucursal_id: number | null;
      ordenes: number;
      servicios: number;
      ingreso: number;
      ingreso_contingencia: number;
      ticket_promedio: number;
    }>(`/dashboard/resumen${query}`);
  },
  ordenes: (filtros: FiltrosOrdenesAdministrativas) => {
    const query = new URLSearchParams();
    if (filtros.sucursalId) query.set('sucursal_id', String(filtros.sucursalId));
    if ('periodo' in filtros.rango) query.set('periodo', String(filtros.rango.periodo));
    else {
      query.set('fecha_inicio', filtros.rango.fecha_inicio);
      query.set('fecha_fin', filtros.rango.fecha_fin);
    }
    query.set('estados', filtros.estados.join(','));
    if (filtros.incluirContingencias) query.set('incluir_contingencias', '1');
    if (filtros.incluirTotal) query.set('incluir_total', '1');
    if (filtros.cursor) {
      query.set('cursor_fecha', filtros.cursor.fecha);
      query.set('cursor_id', String(filtros.cursor.id));
    }
    return api.get<RespuestaOrdenesAdministrativas>(`/dashboard/ordenes?${query.toString()}`);
  },
};

interface ReporteAdminComun {
  periodo: 7 | 14 | 30 | null;
  fecha_inicio: string;
  fecha_fin: string;
  sucursal_id: number | null;
  timezone: string;
  inicio_utc: string;
  fin_utc: string;
}

export interface ReporteAdmin extends ReporteAdminComun {
  kpis: {
    ordenes_cobradas: number;
    servicios_no_cancelados: number;
    servicios_custom: number;
    ventas_iva_incluido: number;
    base_sin_iva_estimada: number;
    iva_estimado: number;
    ticket_promedio: number;
    comisiones_calculadas: number;
    ingresos_cortes: number;
    ingresos_contingencia: number;
    total_operativo: number;
    pendientes_contingencia: number;
    comisiones_contingencia: number;
  };
  serie: Array<{
    fecha: string;
    ventas_iva_incluido: number;
    ordenes_cobradas: number;
    comisiones_calculadas: number;
  }>;
  metodos_pago: Array<{
    metodo: Exclude<MetodoPago, 'pendiente'>;
    ordenes: number;
    monto: number;
  }>;
  lavadores: Array<{
    id: number;
    nombre: string;
    servicios_completados: number;
    comision: number;
  }>;
}

export type ReporteRango = { periodo: 7 | 14 | 30 } | { fecha_inicio: string; fecha_fin: string };

export interface ReporteServicios extends ReporteAdminComun {
  servicios: Array<{
    paquete_id: number | null;
    nombre: string;
    es_custom: boolean;
    cantidad: number;
    ingresos: number;
    descuentos: number;
  }>;
}

export interface ReporteSerieServicio extends ReporteAdminComun {
  serie: Array<{
    fecha: string;
    cantidad: number;
  }>;
}

export interface ReporteLavadores extends ReporteAdminComun {
  pagina: number;
  por_pagina: number;
  total: number;
  total_paginas: number;
  resumen: {
    servicios_completados: number;
    comisiones: number;
    ingresos_atribuidos: number;
  };
  lavadores_disponibles: Array<{
    id: number;
    nombre: string;
  }>;
  lavadores: Array<{
    id: number;
    nombre: string;
    servicios_completados: number;
    comisiones: number;
    ingresos_atribuidos: number;
    tiempo_total_segundos: number;
  }>;
}

export interface ReporteSerieLavador extends ReporteAdminComun {
  lavador: {
    id: number;
    nombre: string;
  };
  servicios_disponibles: Array<{
    paquete_id: number | null;
    nombre: string;
    es_custom: boolean;
  }>;
  serie: Array<{
    fecha: string;
    tiempo_trabajado_segundos: number;
    ingresos_atribuidos: number;
    comisiones: number;
  }>;
}

export interface ReporteServiciosLavador extends ReporteAdminComun {
  lavador: {
    id: number;
    nombre: string;
  };
  pagina: number;
  por_pagina: number;
  total: number;
  total_paginas: number;
  resumen: {
    comisiones: number;
    ingresos_atribuidos: number;
  };
  servicios: Array<{
    id: number;
    fecha_calculo: string;
    folio: string;
    cliente_nombre: string;
    vehiculo_etiqueta: string;
    vehiculo_placas: string | null;
    servicio_nombre: string;
    precio_final: number;
    comision: number;
  }>;
}

function queryReporte(rango: ReporteRango, sucursalId: number | null): string {
  const query = new URLSearchParams();
  if ('periodo' in rango) query.set('periodo', String(rango.periodo));
  else {
    query.set('fecha_inicio', rango.fecha_inicio);
    query.set('fecha_fin', rango.fecha_fin);
  }
  if (sucursalId) query.set('sucursal_id', String(sucursalId));
  return query.toString();
}

export const reportesApi = {
  admin: (rango: ReporteRango, sucursalId: number | null) =>
    api.get<ReporteAdmin>(`/reportes/admin?${queryReporte(rango, sucursalId)}`),
  servicios: (rango: ReporteRango, sucursalId: number | null) =>
    api.get<ReporteServicios>(`/reportes/admin/servicios?${queryReporte(rango, sucursalId)}`),
  serieServicio: (
    rango: ReporteRango,
    sucursalId: number | null,
    servicio: { es_custom: boolean; paquete_id: number | null; nombre: string },
  ) => {
    const query = new URLSearchParams(queryReporte(rango, sucursalId));
    if (servicio.es_custom) {
      query.set('tipo', 'custom');
      query.set('nombre_custom', servicio.nombre);
    } else {
      query.set('tipo', 'paquete');
      query.set('paquete_id', String(servicio.paquete_id));
    }
    return api.get<ReporteSerieServicio>(`/reportes/admin/servicios/serie?${query.toString()}`);
  },
  lavadores: (rango: ReporteRango, sucursalId: number | null, pagina = 1) => {
    const query = new URLSearchParams(queryReporte(rango, sucursalId));
    query.set('pagina', String(pagina));
    return api.get<ReporteLavadores>(`/reportes/admin/lavadores?${query.toString()}`);
  },
  serieLavador: (
    rango: ReporteRango,
    sucursalId: number | null,
    lavadorId: number,
    servicio?: { es_custom: boolean; paquete_id: number | null; nombre: string } | null,
  ) => {
    const query = new URLSearchParams(queryReporte(rango, sucursalId));
    query.set('lavador_id', String(lavadorId));
    if (servicio?.es_custom) {
      query.set('servicio_tipo', 'custom');
      query.set('nombre_custom', servicio.nombre);
    } else if (servicio) {
      query.set('servicio_tipo', 'paquete');
      query.set('paquete_id', String(servicio.paquete_id));
    }
    return api.get<ReporteSerieLavador>(`/reportes/admin/lavadores/serie?${query.toString()}`);
  },
  serviciosLavador: (
    rango: ReporteRango,
    sucursalId: number | null,
    lavadorId: number,
    pagina: number,
    porPagina: 10 | 25 | 50,
    servicio?: { es_custom: boolean; paquete_id: number | null; nombre: string } | null,
  ) => {
    const query = new URLSearchParams(queryReporte(rango, sucursalId));
    query.set('lavador_id', String(lavadorId));
    query.set('pagina', String(pagina));
    query.set('por_pagina', String(porPagina));
    if (servicio?.es_custom) {
      query.set('servicio_tipo', 'custom');
      query.set('nombre_custom', servicio.nombre);
    } else if (servicio) {
      query.set('servicio_tipo', 'paquete');
      query.set('paquete_id', String(servicio.paquete_id));
    }
    return api.get<ReporteServiciosLavador>(
      `/reportes/admin/lavadores/servicios?${query.toString()}`,
    );
  },
};

export interface EventoBitacora {
  id: number;
  usuario_id: number | null;
  usuario_nombre: string | null;
  usuario_email: string | null;
  accion: string;
  recurso: string | null;
  detalle: string | null;
  timestamp: string;
}

export interface BitacoraFiltros {
  fecha_inicio: string;
  fecha_fin: string;
  usuario_id: number | null;
  accion: string | null;
  pagina?: number;
  por_pagina?: number;
}

export const bitacoraApi = {
  filtros: () =>
    api.get<{
      usuarios: Array<{ id: number; nombre: string; email: string }>;
      acciones: string[];
    }>('/bitacora/filtros'),
  listar: (filtros: BitacoraFiltros) => {
    const query = new URLSearchParams({
      fecha_inicio: filtros.fecha_inicio,
      fecha_fin: filtros.fecha_fin,
    });
    if (filtros.usuario_id) query.set('usuario_id', String(filtros.usuario_id));
    if (filtros.accion) query.set('accion', filtros.accion);
    if (filtros.pagina) query.set('pagina', String(filtros.pagina));
    if (filtros.por_pagina) query.set('por_pagina', String(filtros.por_pagina));
    return api.get<{
      fecha_inicio: string;
      fecha_fin: string;
      pagina: number;
      por_pagina: number;
      total: number;
      total_paginas: number;
      eventos: EventoBitacora[];
    }>(`/bitacora?${query.toString()}`);
  },
};

export const lavadoresApi = {
  listar: (sucursalId: number) =>
    api.get<{ lavadores: Lavador[] }>(`/lavadores?sucursal_id=${sucursalId}`),
  crear: (data: { sucursal_id: number; nombre: string; celular?: string }) =>
    api.post<{ lavador: Lavador }>('/lavadores', data),
  editar: (id: number, data: { nombre?: string; celular?: string | null; activo?: boolean }) =>
    api.patch<{ lavador: Lavador }>(`/lavadores/${id}`, data),
  eliminar: (id: number) => api.delete<{ ok: true }>(`/lavadores/${id}`),
  resumen: (sucursalId: number) =>
    api.get<{
      fecha: string;
      lavadores: Array<{
        id: number;
        nombre: string;
        celular: string | null;
        activo: boolean;
        servicios_completados: number;
        comision_generada_hoy: number;
        pagado_hoy: number;
        consumido_hoy: number;
        pendiente_acumulado: number;
      }>;
    }>(`/lavadores/resumen?sucursal_id=${sucursalId}`),
  registrarPago: (id: number, monto: number) =>
    api.post<{
      pago: { id: number; monto: number; tipo: 'pago'; created_at: string };
      pendiente: number;
    }>(`/lavadores/${id}/pagos`, { monto }),
  registrarConsumo: (id: number, monto: number, descripcion: string) =>
    api.post<{
      consumo: {
        id: number;
        monto: number;
        descripcion: string;
        tipo: 'consumo';
        created_at: string;
      };
      pendiente: number;
    }>(`/lavadores/${id}/consumos`, { monto, descripcion }),
  movimientos: (id: number) =>
    api.get<{ pendiente: number; movimientos: MovimientoComision[] }>(
      `/lavadores/${id}/movimientos`,
    ),
  servicios: (id: number, fecha?: string) =>
    api.get<{ fecha: string; servicios: ServicioLavador[] }>(
      `/lavadores/${id}/servicios${fecha ? `?fecha=${encodeURIComponent(fecha)}` : ''}`,
    ),
  revertirPago: (id: number, pagoId: number, motivo: string) =>
    api.post<{ ok: true; pendiente: number }>(`/lavadores/${id}/pagos/${pagoId}/reversa`, {
      motivo,
    }),
};

export const ordenesApi = {
  crear: (data: { sucursal_id: number; cliente_id: number; vehiculo_id: number; notas?: string }) =>
    api.post<OrdenCreada>('/ordenes', data),
  listar: (sucursalId: number, fecha?: string) => {
    const f = fecha ? `&fecha=${fecha}` : '';
    return api.get<{ ordenes: Orden[]; resumen_diario: ResumenDiarioOrdenes }>(
      `/ordenes?sucursal_id=${sucursalId}${f}`,
    );
  },
  pendientesPago: (sucursalId: number, pagina = 1, fecha?: string) =>
    api.get<{
      pagina: number;
      por_pagina: number;
      total: number;
      total_paginas: number;
      ordenes: Orden[];
    }>(
      `/ordenes/pendientes-pago?${new URLSearchParams({
        sucursal_id: String(sucursalId),
        pagina: String(pagina),
        ...(fecha ? { fecha } : {}),
      }).toString()}`,
    ),
  activasAnteriores: (sucursalId: number, pagina = 1, fecha?: string) =>
    api.get<{
      pagina: number;
      por_pagina: number;
      total: number;
      total_paginas: number;
      ordenes: Orden[];
    }>(
      `/ordenes/activas-anteriores?${new URLSearchParams({
        sucursal_id: String(sucursalId),
        pagina: String(pagina),
        ...(fecha ? { fecha } : {}),
      }).toString()}`,
    ),
  get: (id: number) => api.get<{ orden: Orden }>(`/ordenes/${id}`),
  agregarServicio: (
    ordenId: number,
    data: {
      paquete_id?: number;
      paquete_custom?: { nombre: string; precio: number };
      descuento_pct?: number;
      descuento_monto?: number;
      manager_password?: string;
    },
  ) => api.post<ServicioCompleto>(`/ordenes/${ordenId}/servicios`, data),
  eliminarServicio: (ordenId: number, servicioId: number) =>
    api.delete<{ ok: true }>(`/ordenes/${ordenId}/servicios/${servicioId}`),
  actualizarAcabado: (ordenId: number, acabado: AcabadoOrden | null) =>
    api.patch<AcabadoOrdenActualizado>(`/ordenes/${ordenId}/acabado`, { acabado }),
  abrirNotificacionVehiculoListo: (ordenId: number) =>
    api.post<{ notificacion: NotificacionVehiculoListo }>(
      `/ordenes/${ordenId}/notificacion-vehiculo-listo/abrir`,
    ),
  marcarClienteNotificado: (ordenId: number) =>
    api.post<{ notificacion: NotificacionVehiculoListo }>(
      `/ordenes/${ordenId}/notificacion-vehiculo-listo/marcar-notificada`,
    ),
  confirmar: (
    ordenId: number,
    data: { pagos: PagoOrdenInput[]; hora_estimada_entrega: string | null },
  ) =>
    api.post<{ ok: true; estado: string; cambio: number | null }>(
      `/ordenes/${ordenId}/confirmar`,
      data,
    ),
  cobrar: (ordenId: number, data: { pagos: PagoOrdenInput[] }) =>
    api.post<{ ok: true; metodo_pago: MetodoPago; cambio: number | null }>(
      `/ordenes/${ordenId}/cobrar`,
      data,
    ),
  corregirPago: (
    ordenId: number,
    data:
      | { metodo_pago: 'efectivo'; pago_monto_recibido: number }
      | { metodo_pago: 'tarjeta' | 'transferencia'; pago_referencia?: string },
  ) =>
    api.patch<{
      ok: true;
      metodo_pago: MetodoPagoConfirmado;
      pago_referencia: string | null;
      pago_monto_recibido: number | null;
      pago_cambio: number | null;
      confirmed_by: number;
      confirmed_at: string;
    }>(`/ordenes/${ordenId}/pago`, data),
  cancelar: (ordenId: number, motivo?: string) =>
    api.post<{ ok: true }>(`/ordenes/${ordenId}/cancelar`, { motivo }),
  esperar: (ordenId: number) =>
    api.post<{ ok: true; estado: 'en_espera' }>(`/ordenes/${ordenId}/esperar`),
  reanudarCaptura: (ordenId: number) =>
    api.post<{ ok: true; estado: 'draft' }>(`/ordenes/${ordenId}/reanudar-captura`),
  entregar: (ordenId: number) =>
    api.post<{ ok: true; estado: 'entregado' }>(`/ordenes/${ordenId}/entregar`),
};

export type ContingenciaServicioInput = {
  paquete_id: number;
  precio_actual: number;
  lavador_id: number;
};

export const contingenciaApi = {
  capturar: (data: {
    sucursal_id: number;
    cliente_id: number;
    vehiculo_id: number;
    fecha_operativa: string;
    motivo?: string;
    notas?: string;
    servicios: ContingenciaServicioInput[];
    pagos: PagoOrdenInput[];
  }) =>
    api.post<{
      id: number;
      folio: string;
      estado: 'entregado';
      fecha_operativa: string;
      metodo_pago: MetodoPago;
      total: number;
    }>('/admin/contingencia', data),
};

export type PagoOrdenInput = {
  metodo: Exclude<MetodoPago, 'mixto' | 'pendiente'>;
  monto: number;
  referencia?: string;
  efectivo_recibido?: number;
};

export const serviciosApi = {
  cambiar: (
    servicioId: number,
    data: {
      estado?: 'pendiente' | 'en_proceso' | 'pausado' | 'completado' | 'cancelado';
      lavador_id?: number | null;
    },
  ) => api.patch<CambioServicio>(`/servicios/${servicioId}`, data),
  cancelar: (servicioId: number, motivo?: string) =>
    api.post<{ ok: true }>(`/servicios/${servicioId}/cancelar`, { motivo }),
};
